import { FormGroup, Input } from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { PlantDesignDesignerVerifierPage } from 'plantDesign/constants/plantDesignDesignerVerifierPage';
import { capitalizeFirstLetter } from 'shared/utils/Helpers';

export const PersonDetails = React.forwardRef((props, ref) => {
  const {
    value: savedPersonDetails = {},
    onChange = () => {},
    onErrorChange = () => {},
    id = '',
    hasNameError = false,
    nameErrorMessage = '',
  } = props;
  const {
    firstName: savedFirstName = '',
    middleName: savedMiddleName = '',
    lastName: savedLastName = '',
    businessName: savedBusinessName = '',
  } = savedPersonDetails;

  const [firstName, setFirstName] = useState(savedFirstName);
  const [middleName, setMiddleName] = useState(savedMiddleName);
  const [lastName, setLastName] = useState(savedLastName);
  const [businessName, setBusinessName] = useState(savedBusinessName);

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const [errorSummary, setErrorSummary] = useState([]);

  const PAGE_CONSTANT = PlantDesignDesignerVerifierPage.personDetails;

  useEffect(() => {
    let personDetails = {
      firstName: capitalizeFirstLetter(firstName?.toLowerCase()),
      middleName: capitalizeFirstLetter(middleName?.toLowerCase()),
      lastName: capitalizeFirstLetter(lastName?.toLowerCase()),
      businessName,
    };
    onChange(personDetails);

    if (showErrors) validate();
  }, [firstName, middleName, lastName, businessName]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  const validators = [
    {
      fieldName: `${id}_firstName`,
      fieldValue: firstName,
      isValid: (v) => !isEmpty(v) || !isEmpty(lastName),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `${id}_lastName`,
      fieldValue: lastName,
      isValid: (v) => !isEmpty(v) || !isEmpty(firstName),
      errorMessage: () => 'Please enter details',
    },
  ];

  const validate = () => {
    let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

    setShowErrors(!isCurrentComponentValid);

    return isCurrentComponentValid;
  };

  if (ref) {
    ref.current = {
      validate: () => {
        return validate();
      },
    };
  }

  return (
    <>
      <FormGroup
        id={`${id}_firstName`}
        label={PAGE_CONSTANT.firstNameLabel}
        isRequired
        errorMessage={errors[`${id}_firstName`]?.errorMessage || nameErrorMessage}
        hasError={(showErrors && errors[`${id}_firstName`]?.invalid) || hasNameError}
        margin={{ top: 'md' }}
      >
        <Input onChange={(e) => setFirstName(e.target.value)} value={firstName} inputWidth="xl" />
      </FormGroup>
      <FormGroup
        id={`${id}_middleName`}
        label={PAGE_CONSTANT.middleNameLabel}
        errorMessage={errors[`${id}_middleName`]?.errorMessage || nameErrorMessage}
        hasError={(showErrors && errors[`${id}_middleName`]?.invalid) || hasNameError}
        margin={{ top: 'md' }}
      >
        <Input onChange={(e) => setMiddleName(e.target.value)} value={middleName} inputWidth="xl" />
      </FormGroup>
      <FormGroup
        id={`${id}_lastName`}
        label={PAGE_CONSTANT.lastNameLabel}
        isRequired
        errorMessage={errors[`${id}_lastName`]?.errorMessage || nameErrorMessage}
        hasError={(showErrors && errors[`${id}_lastName`]?.invalid) || hasNameError}
        margin={{ top: 'md' }}
      >
        <Input onChange={(e) => setLastName(e.target.value)} value={lastName} inputWidth="xl" />
      </FormGroup>
      <FormGroup
        id={`${id}_businessName`}
        label={PAGE_CONSTANT.businessLabel}
        errorMessage={errors[`${id}_businessName`]?.errorMessage}
        hasError={showErrors && errors[`${id}_businessName`]?.invalid}
        margin={{ top: 'md' }}
      >
        <Input onChange={(e) => setBusinessName(e.target.value)} value={businessName} />
      </FormGroup>
    </>
  );
});

PersonDetails.displayName = 'PersonDetails';

PersonDetails.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  hasNameError: PropTypes.bool,
  nameErrorMessage: PropTypes.string,
};
